'use client';

import {
  DrawerCloseSlot,
  DrawerContentSlot,
  DrawerFooterSlot,
  DrawerHeaderActionsSlot,
  DrawerHeaderSlot,
  DrawerOverlaySlot,
  DrawerTooltipContentSlot,
  DrawerTooltipSlot,
  DrawerTriggerSlot,
} from '@magicschool/spellbook/components/Drawer/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import type { CSSProperties, RefObject } from 'react';
import type { DrawerDirection, DrawerRootProps } from './DrawerPrimitive';
import * as DrawerPrimitive from './DrawerPrimitive';

export type DrawerContentProps = {
  className?: string;
  /** @default 'bottom' */
  direction?: DrawerDirection;
  /** Hides the default X close button. Use when providing custom close actions in footer. */
  disableCloseIcon?: boolean;
  /** Custom class name for the header wrapper element. */
  headerClassName?: string;
  /** Visually hides the header while keeping it accessible to screen readers. Use when content provides sufficient context. */
  hideHeader?: boolean;
  /**
   * Where focus goes when the drawer closes, for a controlled drawer whose opener is not a
   * `<Drawer.Trigger>` because it lives elsewhere in the layout. Without one, focus falls to the page body.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
  style?: CSSProperties;
};

export type DrawerProps = DrawerRootProps & DrawerContentProps;

const DrawerBase: React.FC<DrawerProps> = ({
  children,
  className,
  direction = 'bottom',
  disableCloseIcon = false,
  headerClassName,
  hideHeader = false,
  returnFocusRef,
  style,
  ...props
}) => {
  const { slots } = useSlots(children, {
    overlay: {
      Component: DrawerOverlaySlot,
      props: {
        children,
        contentClassName: className,
        direction,
        style,
        hideHeader,
        headerClassName,
        disableCloseIcon,
        returnFocusRef,
      },
    },
    trigger: { Component: DrawerTriggerSlot },
    tooltip: { Component: DrawerTooltipSlot },
  });

  return (
    <DrawerPrimitive.Root {...props}>
      {slots.tooltip ?? slots.trigger}
      <DrawerPrimitive.Portal>{slots.overlay}</DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
};

/**
 * A slide-out panel for secondary content that doesn't require full page navigation.
 *
 * ### When to use
 * - Preview panels (resource details, thread previews, student info)
 * - Settings or configuration panels that don't need a full page
 * - Mobile navigation menus (use `direction="left"`)
 * - Contextual detail views that supplement the main content
 * - Sharing dialogs with multiple options
 *
 * ### When NOT to use
 * - Confirmation dialogs requiring user decision: use `Modal` instead
 * - Primary navigation on desktop: use a separate view instead
 * - Critical actions needing focused attention: use `Modal` instead
 * - Quick notifications: use `Toast` instead
 *
 * ### Drawer vs Modal vs Sidebar
 * | Component | Position | Use case |
 * |-----------|----------|----------|
 * | **Drawer** | Slides from edge | Secondary content, previews |
 * | **Modal** | Centered overlay | Focused tasks, confirmations |
 *
 * ### API
 * - `direction`: Slide direction (`bottom` default, `top`, `left`, `right`). Use `right` for detail panels, `bottom` for mobile sheets, and do not use `top` or `left` unless your use case truly requires it.
 * - `open` / `onOpenChange`: Controlled state for programmatic open/close
 * - `defaultOpen`: Uncontrolled initial state
 * - `disableCloseIcon`: Hide the default X close button
 * - `hideHeader`: Visually hide header while keeping it accessible to screen readers
 *
 * ### Composition (slots)
 * - `<Drawer.Trigger>`: Wraps the element that opens the drawer so focus returns to it on close; pass `returnFocusRef` instead when the opener lives outside the drawer.
 * - `<Drawer.Header>`: Required. Renders as `<Text element="h2" variant="heading-3" />`.
 * - `<Drawer.Content>`: Main scrollable content area.
 * - `<Drawer.Footer>`: Action buttons area.
 * - `<Drawer.Close>`: Wrap around buttons that should close the drawer.
 * - `<Drawer.HeaderActions>`: Additional header buttons (use sparingly).
 * - `<Drawer.Tooltip>`: Wrap around trigger to show tooltip on hover.
 * - `<Drawer.TooltipContent>`: Tooltip text content.
 *
 * ### Visual states
 * - **Opening**: Slides in from the specified direction with backdrop fade
 * - **Closing**: Slides out with backdrop fade
 *
 * ### Accessibility
 * - Focus is trapped within the drawer while open (handled by Radix Dialog)
 * - Focus returns to trigger on close; a controlled drawer opened from elsewhere passes `returnFocusRef`
 * - Escape key closes the drawer
 * - `aria-labelledby` automatically set from `<Drawer.Header>`
 *
 * ### Direction guidelines
 * - **`bottom`** (default): Mobile-friendly sheets, quick actions.
 * - **`right`**: Detail panels, settings. Keeps main content visible on left.
 * - **`left`**: Main navigation menus, following LTR reading pattern. Common for mobile nav, but avoid for other use cases on desktop.
 * - **`top`**: Rarely used. Consider if your use case truly needs this.
 *
 * @headless Radix Dialog root renders no element; target `drawer-trigger` and `drawer-content`.
 */
export const Drawer = Object.assign(DrawerBase, {
  Trigger: DrawerTriggerSlot,
  Overlay: DrawerOverlaySlot,
  Header: DrawerHeaderSlot,
  Content: DrawerContentSlot,
  Footer: DrawerFooterSlot,
  Close: DrawerCloseSlot,
  HeaderActions: DrawerHeaderActionsSlot,
  Tooltip: DrawerTooltipSlot,
  TooltipContent: DrawerTooltipContentSlot,
});
